import DialogModal from '@/components/DialogModal';
import ScheduledWeeklyTimeSlot from '@/components/DragAndDropCalendar/TimeSlot';
import { DayToolbar } from '@/components/DragAndDropCalendar/Toolbar';
import { CalendarWeeklyHeader } from '@/components/DragAndDropCalendar/WeekHeader';
import { useCalendar } from '@/components/DragAndDropCalendar/context';
import { DAY, WEEK } from '@/constants/calendar';
import { useUpdateAction } from '@/services/action/hooks';
import {
  useCronofyCheckCalendarPermissions,
  useCronofyManageExternalEvent,
  useCronofyRequestExtendedPermissions,
} from '@/services/cronofy/hooks';
import { queryClient } from '@/services/graphql/queryClient';
import { useActionModal } from '@/stores/useActionModal';
import useSettingsStore from '@/stores/useSettings';
import { MyEvent } from '@/types/calendar';
import { checkEventStyles, getGMTOffset, localDateToUTC, localizer, minutesToDurationString } from '@/utils/calendar';
import { CronofyExtendedPermissionsManager, hasUnrestrictedPermissions } from '@/utils/cronofyExtendedPermissions';
import rem from '@/utils/rem';
import { refetchQueries } from '@/utils/tanStackQuery';
import { Box, VStack, useDisclosure, useToast, useToken } from '@chakra-ui/react';
import { useDndMonitor, useDroppable } from '@dnd-kit/core';
import { addMinutes, differenceInMinutes, format, getMonth } from 'date-fns';
import { motion } from 'framer-motion';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Calendar, ToolbarProps } from 'react-big-calendar';
import withDragAndDrop, {
  DragFromOutsideItemArgs,
  EventInteractionArgs,
} from 'react-big-calendar/lib/addons/dragAndDrop';
import { useTranslation } from 'react-i18next';

import CalendarProvider from './provider';

type CalendarResource = {
  id: string;
};

function CalendarToolbar({ view, ...props }: ToolbarProps<MyEvent, CalendarResource>) {
  if (view === DAY) return <DayToolbar {...props} />;
  return null;
}

type DragAndDropCalendarProps = {
  date?: Date;
  eventResizable?: boolean;
  height?: number | string;
  view: typeof DAY | typeof WEEK;
  onEntityDropped: (entityId: string, date: Date) => void;
  onDurationChange: (event: MyEvent, start: Date, duration: string) => void;
  onTimeChange: (event: MyEvent, date: Date) => void;
  onUnplan: (event: MyEvent) => void;
  onClickEvent: (event: MyEvent) => void;
  setCombinedEvents?: (event: MyEvent[]) => void;
};

type SlotProps = {
  start: Date;
  end: Date;
  slots: Date[];
  action: 'select' | 'click' | 'doubleClick';
};

const DragAndDropCalendar = withDragAndDrop<MyEvent, CalendarResource>(Calendar);

const DROPPABLE_CONTAINER_ID = 'dailyView';
const TIME_STEP = 15; // 15 minute blocks on calendar.
const MINUTES_OFFSET = -120; // offset of minutes to show the current time more in view.
const MINUTES_IN_HOUR = 60; // 1 hour
const HOURS_IN_DAY = 24; // 24 hours in day.
const CALENDAR_MAX_HEIGHT = 2774;

function getSlotCount(timeStep: number): number {
  return HOURS_IN_DAY / (timeStep / MINUTES_IN_HOUR);
}

function Component({
  date = new Date(),
  eventResizable = true,
  height = 760,
  view,
  onEntityDropped,
  onDurationChange,
  onTimeChange,
  onUnplan,
  onClickEvent,
  setCombinedEvents,
}: DragAndDropCalendarProps) {
  const { t } = useTranslation('EventDetailModal');
  const { combinedEvents } = useCalendar();
  const { setActionModalOpen } = useActionModal();
  const updateActionMutation = useUpdateAction();

  const { showDeclinedEvents } = useSettingsStore();

  const [draggedOutsideElement, setDraggedOutsideElement] = useState<string | null>(null);
  const [pendingExternalEventMove, setPendingExternalEventMove] = useState<{
    event: MyEvent;
    newStart: Date;
    newEnd: Date;
  } | null>(null);

  const { setNodeRef } = useDroppable({ id: DROPPABLE_CONTAINER_ID });

  const {
    isOpen: isRescheduleModalOpen,
    onOpen: onOpenRescheduleModal,
    onClose: onCloseRescheduleModal,
  } = useDisclosure();

  const {
    isOpen: isPermissionsModalOpen,
    onOpen: onOpenPermissionsModal,
    onClose: onClosePermissionsModal,
  } = useDisclosure();

  const { mutateAsync: updateExternalEvent } = useCronofyManageExternalEvent({
    onSuccess: () => {
      const queryCache = queryClient.getQueryCache();
      const cronofyEventQueries = queryCache
        .getAll()
        .filter((query) => query.queryKey.includes('cronofy') && query.queryKey.includes('events'));

      const clientQueryKeys = cronofyEventQueries.map((q) => q.queryKey);

      setTimeout(() => {
        refetchQueries(clientQueryKeys);
        toast({
          title: t('toasts.eventRescheduled'),
          status: 'success',
          duration: 3000,
          isClosable: true,
        });
        setIsRescheduleLoading(false);
        onCloseRescheduleModal();
        // In case of some delay, we need to refetch again the queries to update the event in the calendar
        setTimeout(() => {
          refetchQueries(clientQueryKeys);
        }, 1000);
      }, 3000);
    },
  });

  const { mutateAsync: requestExtendedPermissions } = useCronofyRequestExtendedPermissions();

  const [isRescheduleLoading, setIsRescheduleLoading] = useState(false);
  const [isRequestingPermissions, setIsRequestingPermissions] = useState(false);

  const toast = useToast();

  // Extended Permissions Manager
  const permissionsManager = useMemo(
    () => new CronofyExtendedPermissionsManager(requestExtendedPermissions),
    [requestExtendedPermissions],
  );

  // Check calendar permissions for external events
  const calendarIds = useMemo(() => {
    if (pendingExternalEventMove?.event?.calendarId) {
      return [pendingExternalEventMove.event.calendarId];
    }
    return [];
  }, [pendingExternalEventMove]);

  const { data: calendarPermissions, refetch: refetchCalendarPermissions } = useCronofyCheckCalendarPermissions(
    calendarIds,
    calendarIds.length > 0,
  );

  // Check if calendar has unrestricted permissions
  const hasUnrestrictedAccess = useMemo(() => {
    if (!pendingExternalEventMove?.event?.calendarId || !calendarPermissions) return false;
    return hasUnrestrictedPermissions(calendarPermissions, [pendingExternalEventMove.event.calendarId]);
  }, [pendingExternalEventMove, calendarPermissions]);

  const [fallbackColor, nowColor] = useToken('colors', ['gray.500', 'cyan.300']);

  const handleEventResize = useCallback(
    ({ event, start, end }: EventInteractionArgs<MyEvent>) => {
      // get difference in minutes;
      const totalMinutes = differenceInMinutes(new Date(end), new Date(start));
      const duration = minutesToDurationString(totalMinutes);
      if (totalMinutes === 0) return;

      onDurationChange(event, new Date(start), duration);
    },
    [onDurationChange],
  );

  const handleEventDrop = useCallback(
    ({ event, start, end }: EventInteractionArgs<MyEvent>) => {
      // Check if this is an external event
      if (event.isExternal && event.calendarId && event.id) {
        // Store the pending move
        setPendingExternalEventMove({
          event,
          newStart: new Date(start),
          newEnd: new Date(end),
        });
      } else {
        // Handle internal events as before
        onTimeChange(event, new Date(start));
      }
    },
    [onTimeChange],
  );

  const pointerPosition = useRef({ x: 0, y: 0 });

  useEffect(() => {
    function handleMove(e: PointerEvent) {
      pointerPosition.current = {
        x: e.clientX,
        y: e.clientY,
      };
    }

    window.addEventListener('pointermove', handleMove);

    return () => {
      window.removeEventListener('pointermove', handleMove);
    };
  }, []);

  useEffect(() => {
    setCombinedEvents && setCombinedEvents(combinedEvents);
  }, [combinedEvents, setCombinedEvents]);

  // Check permissions when pendingExternalEventMove changes
  useEffect(() => {
    if (pendingExternalEventMove && calendarPermissions) {
      if (hasUnrestrictedAccess) {
        // User has permissions, proceed with reschedule confirmation
        onOpenRescheduleModal();
      } else {
        // User doesn't have permissions, show permissions modal
        onOpenPermissionsModal();
      }
    }
  }, [
    pendingExternalEventMove,
    calendarPermissions,
    hasUnrestrictedAccess,
    onOpenRescheduleModal,
    onOpenPermissionsModal,
  ]);

  useDndMonitor({
    onDragStart(event) {
      setDraggedOutsideElement(event.active.id.toString());
    },
    onDragCancel() {
      setDraggedOutsideElement(null);
    },
    onDragEnd({ over }) {
      setDraggedOutsideElement(null);
      if (over?.id !== DROPPABLE_CONTAINER_ID || !draggedOutsideElement) {
        return;
      }

      // Target the react big calendar containers.
      const dailyViewContainer = document.querySelector('.rbc-time-content');
      const dailyColumn = document.querySelector('.rbc-time-column');

      if (!dailyViewContainer || !dailyColumn) {
        return;
      }

      // Find the closest .rbc-timeslot-group
      const dailyColumnRect = dailyColumn.getBoundingClientRect();
      const viewportRect = dailyViewContainer.getBoundingClientRect();

      // Convert to local space
      const scrolledDifference = viewportRect.top - dailyColumnRect.top;
      const mouseDifference = pointerPosition.current.y - viewportRect.top;
      const localPosition = scrolledDifference + mouseDifference;

      // Get the minute offset position
      const numOfSlots = getSlotCount(TIME_STEP);
      const shouldGoInSlot = Math.floor((localPosition / dailyColumnRect.height) * numOfSlots);
      const totalMinutes = shouldGoInSlot * TIME_STEP;

      // Convert minutes to hour and minutes
      const hours = Math.floor(totalMinutes / MINUTES_IN_HOUR);
      const minutes = totalMinutes - hours * MINUTES_IN_HOUR;

      // Selected date set to hours minutes
      const newDateTime = new Date(date);
      newDateTime.setHours(hours);
      newDateTime.setMinutes(minutes);

      if (view === WEEK) {
        const headerContainer = document.querySelector('.rbc-time-header-content');
        const daysOfWeek = document.querySelectorAll('.rbc-header');

        if (!headerContainer || !daysOfWeek) {
          return;
        }

        const headerContainerRect = headerContainer.getBoundingClientRect();

        const scrolledDifferenceX = viewportRect.left - headerContainerRect.left;
        const mouseDifferenceX = pointerPosition.current.x - viewportRect.left;
        const localPositionX = scrolledDifferenceX + mouseDifferenceX;

        const index = Math.floor((localPositionX / headerContainerRect.width) * 7);

        const dayOfWeek = daysOfWeek[index];
        const droppedDateString = (dayOfWeek.querySelector('[data-date]') as HTMLElement).dataset.date;

        if (!droppedDateString) {
          toast({
            title: 'Something happened, please try again!',
            status: 'error',
            duration: 3000,
            isClosable: true,
          });
          return;
        }

        const droppedDate = new Date(droppedDateString);
        const day = format(droppedDate, 'dd');
        const month = getMonth(droppedDate);

        newDateTime.setDate(Number(day));
        newDateTime.setMonth(Number(month));
      }

      // Update action and create a new event
      onEntityDropped(draggedOutsideElement, newDateTime);
    },
  });

  const onDropFromOutside = useCallback(
    (e: DragFromOutsideItemArgs) => {
      if (!draggedOutsideElement) {
        return;
      }

      const event = {
        id: draggedOutsideElement,
      } as MyEvent;

      handleEventDrop({ event, start: e.start, end: e.end, isAllDay: e.allDay });

      setDraggedOutsideElement('');
    },
    [draggedOutsideElement, handleEventDrop],
  );

  const TimeSlotWrapper = useCallback(
    ({ value, children }: any) => (
      <Box color={value.getHours() === new Date().getHours() ? nowColor : fallbackColor}>{children}</Box>
    ),
    [fallbackColor, nowColor],
  );

  const containerHeight = useMemo(() => {
    if (typeof height === 'string') {
      return height;
    }

    if (height > CALENDAR_MAX_HEIGHT) {
      return `${CALENDAR_MAX_HEIGHT}px`;
    }

    return `${height}px`;
  }, [height]);

  const updateEventDate = useCallback(
    (actionId: string, scheduledDate: string, scheduledTime: string | null, gmtOffset?: string) => {
      updateActionMutation.mutate({
        actionId,
        set: {
          scheduledDate,
          scheduledTime,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          gmtOffset: gmtOffset ?? getGMTOffset(Intl.DateTimeFormat().resolvedOptions().timeZone),
        },
      });
    },
    [updateActionMutation],
  );

  const handleSlotSelected = (slotInfo: SlotProps) => {
    const { scheduledDate, scheduledTime, gmtOffset } = localDateToUTC(slotInfo.start);
    setActionModalOpen(true, {
      preSelectedDate: slotInfo.start,
      preSelectedTime: slotInfo.start,
      defaultActionDuration: '00:30',
      onCreateAction: (action) => {
        if (!action.scheduledTime) {
          updateEventDate(action.id, scheduledDate, scheduledTime, gmtOffset);
        }
      },
    });
  };

  const handleRequestPermissions = async () => {
    if (!pendingExternalEventMove?.event?.calendarId) return;
    const calendarId = pendingExternalEventMove.event.calendarId;

    setIsRequestingPermissions(true);

    // CRITICAL: Open popup IMMEDIATELY to avoid browser blocking
    // This must be the first action, before any async operations
    const popup = permissionsManager.openPermissionsPopupImmediate();

    if (!popup) {
      return;
    }

    try {
      const result = await permissionsManager.requestExtendedPermissionsWithPopup([calendarId], popup, t);

      if (result.success) {
        // Refetch permissions and get fresh data
        const { data: freshCalendarPermissions } = await refetchCalendarPermissions();

        // Calculate permissions directly with fresh data
        const hasUnrestrictedAccessNow = freshCalendarPermissions
          ? hasUnrestrictedPermissions(freshCalendarPermissions, [calendarId])
          : false;

        if (hasUnrestrictedAccessNow) {
          // After permissions are granted, proceed with reschedule
          onOpenRescheduleModal();
        } else {
          toast({
            title: t('toasts.permissionsDenied'),
            description: result.error,
            status: 'warning',
            duration: 5000,
            isClosable: true,
          });
        }
        onClosePermissionsModal();
      } else {
        toast({
          title: t('toasts.permissionsDenied'),
          description: result.error,
          status: 'warning',
          duration: 5000,
          isClosable: true,
        });
      }
    } catch (error) {
      console.error('Error requesting extended permissions:', error);
      // Make sure to close popup on error
      if (!popup.closed) {
        popup.close();
      }
      toast({
        title: t('toasts.permissionsError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsRequestingPermissions(false);
    }
  };

  const handleConfirmReschedule = async () => {
    if (!pendingExternalEventMove) return;

    const { event, newStart, newEnd } = pendingExternalEventMove;

    setIsRescheduleLoading(true);

    try {
      await updateExternalEvent({
        calendarId: event.calendarId!,
        eventId: event.id,
        operation: 'update',
        eventData: {
          eventId: event.id,
          summary: event.title,
          description: event.description,
          start: newStart.toISOString(),
          end: newEnd.toISOString(),
          tzid: Intl.DateTimeFormat().resolvedOptions().timeZone,
        },
      });
    } catch (error) {
      console.error('Error rescheduling external event:', error);
      toast({
        title: t('toasts.eventRescheduleError'),
        description: error instanceof Error ? error.message : 'Unknown error',
        status: 'error',
        duration: 5000,
        isClosable: true,
      });
      setIsRescheduleLoading(false);
      setPendingExternalEventMove(null);
      onCloseRescheduleModal();
    }
  };

  const handleCancelReschedule = () => {
    setPendingExternalEventMove(null);
    onCloseRescheduleModal();
  };

  const handleCancelPermissions = () => {
    setPendingExternalEventMove(null);
    onClosePermissionsModal();
  };

  function getCombinedEvents(combinedEvents: any[]): any[] {
    const targetIndex = combinedEvents.findIndex((event) => Array.isArray(event));

    if (targetIndex !== -1) {
      const targetArray = combinedEvents[targetIndex] as any[];
      return [...combinedEvents, ...targetArray];
    }

    return combinedEvents;
  }

  return (
    <VStack ref={setNodeRef} position="relative" overflow="hidden" width="full" height={containerHeight}>
      <motion.div
        layout
        initial={{ opacity: 0, width: '100%', flex: 1, overflow: 'hidden' }}
        animate={{ opacity: 1, width: '100%' }}
        exit={{ opacity: 0 }}
        transition={{ duration: 0.5 }}
        style={{
          height: 'inherit',
          overflowX: 'auto',
          overflowY: 'hidden',
        }}
      >
        <DragAndDropCalendar
          selectable
          onSelectSlot={handleSlotSelected}
          onEventDrop={handleEventDrop}
          // onDragStart={(e) => setDraggedOutsideElement(String(e.event.id))}
          onDropFromOutside={onDropFromOutside}
          onEventResize={eventResizable ? handleEventResize : undefined}
          className={view === WEEK ? 'extended-calendar schedule-calendar' : 'extended-calendar day'}
          showMultiDayTimes
          views={['day', 'week']}
          events={getCombinedEvents(combinedEvents)}
          step={TIME_STEP} // duration of the slot
          timeslots={4}
          defaultDate={addMinutes(new Date(), MINUTES_OFFSET)} // rollback time to show current time more in view.
          date={date}
          view={view}
          localizer={localizer}
          dayLayoutAlgorithm={view === DAY ? 'no-overlap' : 'overlap'}
          startAccessor="start"
          endAccessor="end"
          onNavigate={() => null}
          onView={() => null}
          draggableAccessor={(event) => true}
          resizableAccessor={(event) => eventResizable && !(event.resource.isExternal || event.resource.planned)}
          allDayAccessor={(event) => event.resource.planned}
          formats={{
            timeGutterFormat: 'h a',
          }}
          popup
          allDayMaxRows={2}
          scrollToTime={addMinutes(new Date(), MINUTES_OFFSET)} // show 2 hours before so that the current time is more centered.
          components={{
            toolbar: CalendarToolbar,
            day: {
              header: () => <></>,
              event: (props) => (
                <ScheduledWeeklyTimeSlot {...props} onUnplan={onUnplan} onClick={onClickEvent} minHeight={rem(15)} />
              ),
            },
            week: {
              header: CalendarWeeklyHeader,
              event: (props) => (
                <ScheduledWeeklyTimeSlot {...props} onUnplan={onUnplan} onClick={onClickEvent} minHeight={rem(15)} />
              ),
            },
            timeSlotWrapper: view === WEEK ? TimeSlotWrapper : undefined,
          }}
          eventPropGetter={(event) => {
            return {
              className:
                !event.allDay && differenceInMinutes(event.end, event.start) < 30 ? 'short-duration' : undefined,
              style: {
                background: 'none',
                border: 'none',
                overflow: 'unset',
                padding: 0,
                margin: 0,
                boxShadow: '2px 2px 9px 0px rgba(0, 0, 0, 0.25)',
                ...checkEventStyles(event?.participationStatus || '', showDeclinedEvents),
              },
            };
          }}
        />
      </motion.div>

      {/* Reschedule Confirmation Modal */}
      <DialogModal
        isOpen={isRescheduleModalOpen}
        onCancel={handleCancelReschedule}
        title={t('rescheduleDialog.title')}
        message={t('rescheduleDialog.message')}
        confirmText={t('rescheduleDialog.confirmButton')}
        cancelText={t('rescheduleDialog.cancelButton')}
        onConfirm={handleConfirmReschedule}
        confirmVariant="primary"
        isButtonConfirmLoading={isRescheduleLoading}
      />

      {/* Permissions Modal */}
      <DialogModal
        isOpen={isPermissionsModalOpen}
        onCancel={handleCancelPermissions}
        title={t('permissionsDialog.title')}
        message={t('permissionsDialog.message')}
        confirmText={t('permissionsDialog.grantButton')}
        cancelText={t('permissionsDialog.cancelButton')}
        onConfirm={handleRequestPermissions}
        isButtonConfirmLoading={isRequestingPermissions}
        confirmVariant="primary"
      />
    </VStack>
  );
}

export function DragAndDropCalendarWrapper({ date = new Date(), view, ...rest }: DragAndDropCalendarProps) {
  return (
    <CalendarProvider date={date} view={view}>
      <Component date={date} view={view} {...rest} />
    </CalendarProvider>
  );
}
